'use client'

import { EmptyState } from '@/app/components/shared/EmptyState'
import { InfiniteVirtualizedList } from '@/app/components/shared/InfiniteVirtualizedList'
import { HorizontalCard } from '@/app/components/shared/cards/HorizontalCard'
import { VerticalCard } from '@/app/components/shared/cards/VerticalCard'
import { useViewModeContext } from '@/app/context/viewMode'
import { isLinkableProduct, mapProductToCard } from '@/app/lib/api/mapProductToCard'
import type { ProductApiItem } from '@/app/lib/api/types-api'
import { COLUMN_GAP, COLUMN_GAP_PX, PAGE_PARAM, ROW_GAP_PX, SCROLL_THRESHOLD } from '@/app/lib/constants'
import { useIsDesktopViewport } from '@/app/lib/hooks/useIsDesktopViewport'
import { computeHorizontalColumns, useResponsiveColumns } from '@/app/lib/hooks/useResponsiveColumns'
import { estimateHorizontalRowHeight, estimateRowHeight } from '@/app/lib/utils'

import { fetchRecintoEventsPageAction } from '@/app/entradas/(general-web)/recintos/[location]/[url]/_components/actions'

import type { RecintoEventsResultsProps } from './RecintoEventsResults.types'

/**
 * `RecintoEventsResults` — adaptador fino de `InfiniteVirtualizedList` con
 * los eventos de un recinto. Segunda iteración de `041-pagina-recinto`:
 * `fetchPage` ya no corta un array en memoria (`slicePage`) — llama a
 * `fetchRecintoEventsPageAction`, que reconsulta `getResourceByUrl` con la
 * página solicitada, igual que `SubcategoriaResults`/`fetchSubcategoriaPageAction`.
 *
 * La vista horizontal/vertical la controla `ViewModeContext` (compartido con
 * el `VistaToggle` de `RecintoEventsFilters`). Requiere `<ViewModeProvider>`
 * como ancestro. El selector no está disponible en mobile/tablet, así que
 * `isDesktop` fuerza `VerticalCard` ahí con independencia del `viewMode` del
 * contexto — mismo patrón que el resto de PLPs virtualizadas del sitio.
 */
export function RecintoEventsResults({
  initialItems,
  initialPage,
  totalPages,
  pageSize,
  location,
  url,
  filters,
  paginationLinks,
}: RecintoEventsResultsProps) {
  const { viewMode } = useViewModeContext()
  const isDesktop = useIsDesktopViewport()
  const isHorizontal = viewMode === 'horizontal' && isDesktop

  const { columns, estimatedRowHeight } = useResponsiveColumns({
    columnGapPx: COLUMN_GAP_PX,
    estimateRowHeight: isHorizontal ? estimateHorizontalRowHeight : estimateRowHeight,
    columnsOverride: isHorizontal ? computeHorizontalColumns : undefined,
  })

  // `isLinkableProduct` descarta productos sin ficha (PDP) derivable — p. ej.
  // llegan sin `genres` — para no pintar una card sin destino ni en la carga
  // inicial ni en las páginas siguientes del scroll infinito (mismo criterio
  // que `toProductCards`/`toGridCards`, `mapProductToCard.ts`). `totalPages`
  // no se recalcula: viene tal cual del backend, así que filtrar aquí no
  // desincroniza la paginación de `InfiniteVirtualizedList` (solo puede
  // resultar en menos de `pageSize` cards visibles en alguna página).
  return (
    <InfiniteVirtualizedList<ProductApiItem>
      initialItems={initialItems.filter(isLinkableProduct)}
      initialPage={initialPage}
      totalPages={totalPages}
      pageSize={pageSize}
      threshold={SCROLL_THRESHOLD}
      getItemKey={(item) => item.id}
      fetchPage={async (page) => {
        const result = await fetchRecintoEventsPageAction(location, url, filters, page)
        return { ...result, items: result.items.filter(isLinkableProduct) }
      }}
      renderItem={(item) =>
        isHorizontal ? <HorizontalCard {...mapProductToCard(item)} /> : <VerticalCard {...mapProductToCard(item)} />
      }
      estimatedRowHeight={estimatedRowHeight}
      columns={columns}
      columnGap={COLUMN_GAP}
      rowGap={ROW_GAP_PX}
      pageParam={PAGE_PARAM}
      paginationLinks={paginationLinks}
      errorMessage="No se han podido cargar más eventos."
      loadingLabel="Cargando más eventos"
      emptyState={
        <EmptyState
          title="No hay eventos disponibles"
          description="Prueba a cambiar de filtros para ver más eventos de este recinto."
        />
      }
    />
  )
}
